import { Parameter, RangeValue } from '@shared/types';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getParameterIcon } from '@/utils/parameterUtils';
import { ParameterLabel } from '@/components/parameter/ParameterLabel';
import { useState, useEffect } from 'react';

export function RangeInput({
  param,
  onCommit,
}: {
  param: Parameter;
  onCommit: (value: RangeValue) => void;
}) {
  const [localValue, setLocalValue] = useState<RangeValue>(
    (param.value as RangeValue) || (param.defaultValue as RangeValue),
  );

  useEffect(() => {
    setLocalValue(
      (param.value as RangeValue) || (param.defaultValue as RangeValue),
    );
  }, [param.value, param.defaultValue]);

  const updateField = (field: keyof RangeValue, valStr: string) => {
    const num = parseFloat(valStr);

    setLocalValue((prev) => {
      const next = { ...prev };
      if (field === 'step' && valStr === '') {
        next.step = null;
      } else if (!isNaN(num)) {
        next[field] = num as any;
      }
      return next;
    });
  };

  const commit = () => {
    const finalValue = { ...localValue };

    // Apply min/max constraints to both start and end
    // This allows for both ascending ranges [0:10] and descending ranges [10:0]
    // as long as all values are within bounds.
    // Only apply when the bound has already been resolved to a concrete number.
    const rangeMin =
      typeof param.range?.min === 'number' ? param.range.min : undefined;
    const rangeMax =
      typeof param.range?.max === 'number' ? param.range.max : undefined;

    if (rangeMin !== undefined) {
      if (finalValue.start < rangeMin) finalValue.start = rangeMin;
      if (finalValue.end < rangeMin) finalValue.end = rangeMin;
    }

    if (rangeMax !== undefined) {
      if (finalValue.start > rangeMax) finalValue.start = rangeMax;
      if (finalValue.end > rangeMax) finalValue.end = rangeMax;
    }

    setLocalValue(finalValue);
    onCommit(finalValue);
  };

  const IconComponent = getParameterIcon(param.name);

  return (
    <div className="grid w-full grid-cols-[96px_1fr] items-start gap-3">
      <ParameterLabel
        param={param}
        IconComponent={IconComponent}
        className="pt-2"
      />
      <div className="grid grid-cols-3 gap-2">
        <div className="flex flex-col gap-1">
          <Label className="pl-1 text-[10px] text-muted-foreground">
            Start
          </Label>
          <Input
            value={localValue.start}
            onChange={(e) => updateField('start', e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => e.key === 'Enter' && commit()}
            className="h-7 px-2 text-center text-xs"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="pl-1 text-[10px] text-muted-foreground">Step</Label>
          <Input
            value={localValue.step === null ? '' : localValue.step}
            placeholder="1"
            onChange={(e) => updateField('step', e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => e.key === 'Enter' && commit()}
            className="h-7 px-2 text-center text-xs"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="pl-1 text-[10px] text-muted-foreground">End</Label>
          <Input
            value={localValue.end}
            onChange={(e) => updateField('end', e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => e.key === 'Enter' && commit()}
            className="h-7 px-2 text-center text-xs"
          />
        </div>
      </div>
    </div>
  );
}
